// frontend/mobile/src/stores/operationStore.ts
// 運行状態管理Store - フェーズ管理機能追加版
// 🔧 修正: phase, loadingLocation, unloadingLocation フィールドとアクション追加 (2025-12-12)
// 🔧 修正: previousPhase フィールドとアクション追加 (2025-12-28) - 休憩終了時のフェーズ復元対応

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * 🆕 運行フェーズ型定義
 */
export type OperationPhase = 
  | 'TO_LOADING' 
  | 'AT_LOADING' 
  | 'LOADING_IN_PROGRESS'      // 積込作業中（P1のみ: S〜E 時間計測中）
  | 'TO_UNLOADING' 
  | 'AT_UNLOADING' 
  | 'UNLOADING_IN_PROGRESS'    // 荷降作業中（U1のみ: S〜E 時間計測中）
  | 'BREAK' 
  | 'REFUEL';

/**
 * 運行状態インターフェース
 * 🔧 修正: phase, loadingLocation, unloadingLocation フィールド追加
 * 🔧 修正: previousPhase フィールド追加 (2025-12-28)
 */
export interface OperationState {
  // 運行情報
  operationId: string | null;
  operationStartTime: string | null;  // 🆕 運行開始時刻（ISO文字列, 永続化対象）
                                       //    ブラウザを閉じても経過時間を正確に復元するために使用
  currentBreakStartTime: string | null;  // 🔧 [経過時間再修正] 休憩開始時刻（ISO文字列, 永続化対象）
  totalBreakSeconds: number;             // 🔧 [経過時間再修正] 完了した休憩の合計秒数（永続化対象）
  totalDistanceKm?: number | null;  // ✅ Fix-S11-8: GPS累積走行距離
  vehicleId: string | null;
  vehicleNumber: string | null;
  vehicleType: string | null;
  vehicleCapacity: number | null;  // REQ-004: 車両積載量
  driverId: string | null;
  driverName: string | null;
  startMileage: number | null;
  
  // 🆕 運行フェーズ管理
  phase: OperationPhase;
  breakCount: number;                              // 🔧 休憩回数（永続化対象）(2026-02-01)
  previousPhase: OperationPhase | null; // 🆕 休憩前のフェーズを記憶 (2025-12-28)
  loadingLocation: string | null;
  loadingLocationId: string | null;   // REQ-019: 積込場所ID
  loadingLocationLat: number | null;   // 🆕 積込場所緯度
  loadingLocationLng: number | null;   // 🆕 積込場所経度
  unloadingLocation: string | null;
  unloadingLocationId: string | null;
  customerId: string | null;      // 🆕 客先ID
  customerName: string | null;    // 🆕 客先名
  // 🆕 オペレーションパターン（車両別設定）
  loadingPattern: number;         // 1=開始+完了, 2=品目選択後完了のみ, 3=即時完了
  unloadingPattern: number;       // 1=開始+完了, 2=到着後完了のみ, 3=即時完了
  
  // 運行ステータス
  status: 'IDLE' | 'INSPECTING' | 'IN_PROGRESS' | 'COMPLETED';
  
  // 点検完了フラグ
  inspectionCompleted: boolean;
  inspectionRecordId: string | null;
  
  // Actions
  setVehicleInfo: (info: {
    vehicleId: string;
    vehicleNumber: string;
    vehicleType: string;
    startMileage: number;
    capacity?: number;  // REQ-004
    loadingPattern?: number;   // 🆕 オペレーションパターン
    unloadingPattern?: number; // 🆕 オペレーションパターン
  }) => void;
  
  setDriverInfo: (info: {
    driverId: string;
    driverName: string;
  }) => void;
  
  setInspectionCompleted: (recordId: string) => void;
  
  startOperation: (operationId: string, startTime?: string) => void;  // 🆕 startTime引数追加
  setOperationStartTime: (startTime: string) => void;  // 🆕 運行開始時刻の後付け設定（復元フォールバック用）
  startBreakTimer: () => void;  // 🔧 [経過時間再修正] 休憩開始時刻を永続化
  endBreakTimer: () => void;    // 🔧 [経過時間再修正] 休憩終了時に合計秒数へ加算
  
  // 🆕 フェーズ管理アクション
  setPhase: (phase: OperationPhase) => void;
  savePreviousPhase: (phase: OperationPhase) => void; // 🆕 休憩前フェーズ保存 (2025-12-28)
  incrementBreakCount: () => void;              // 🔧 休憩回数インクリメント (2026-02-01)
  setLoadingLocation: (location: string, locationId?: string | null) => void;  // REQ-019
  setLoadingLocationWithCoords: (location: string, lat: number, lng: number) => void; // 🆕
  setUnloadingLocation: (location: string, locationId?: string) => void;
  setCustomerInfo: (info: { customerId: string; customerName: string }) => void;
  setTotalDistanceKm: (km: number) => void;  // ✅ Fix-S11-8 // 🆕
  
  completeOperation: () => void;
  
  resetOperation: () => void;
}

/**
 * 運行状態管理Store
 */
export const useOperationStore = create<OperationState>()(
  persist(
    (set, get) => ({
      // 初期状態
      operationId: null,
      operationStartTime: null,  // 🆕
      currentBreakStartTime: null,  // 🔧 [経過時間再修正]
      totalBreakSeconds: 0,         // 🔧 [経過時間再修正]
      vehicleId: null,
      vehicleNumber: null,
      vehicleType: null,
      vehicleCapacity: null,  // REQ-004
      driverId: null,
      driverName: null,
      startMileage: null,
      status: 'IDLE',
      inspectionCompleted: false,
      inspectionRecordId: null,
      
      // 🆕 フェーズ管理初期値
      phase: 'TO_LOADING',
      breakCount: 0,
      previousPhase: null,
      loadingLocation: null,
      loadingLocationId: null,   // REQ-019
      loadingLocationLat: null,   // 🆕
      loadingLocationLng: null,   // 🆕
      unloadingLocation: null,
  unloadingLocationId: null,
      customerId: null,           // 🆕
      customerName: null,         // 🆕
      loadingPattern: 2,          // 🆕 デフォルト: P2（品目選択後完了のみ）
      unloadingPattern: 2,        // 🆕 デフォルト: U2（到着後完了のみ）
      totalDistanceKm: null,      // ✅ Fix-S11-8

      // Actions
      setVehicleInfo: (info) => {
        console.log('[Operation Store] ✅ Set vehicle info:', info);
        set({
          vehicleId: info.vehicleId,
          vehicleNumber: info.vehicleNumber,
          vehicleType: info.vehicleType,
          startMileage: info.startMileage,
          vehicleCapacity: info.capacity ?? null,  // REQ-004
          loadingPattern: info.loadingPattern ?? 2,    // 🆕
          unloadingPattern: info.unloadingPattern ?? 2, // 🆕
          status: 'IDLE'
        });
        
        // デバッグ: 設定後の状態確認
        const currentState = get();
        console.log('[Operation Store] 📊 Current state after setVehicleInfo:', {
          vehicleId: currentState.vehicleId,
          vehicleNumber: currentState.vehicleNumber,
          status: currentState.status
        });
      },

      setDriverInfo: (info) => {
        console.log('[Operation Store] ✅ Set driver info:', info);
        set({
          driverId: info.driverId,
          driverName: info.driverName
        });
        
        const currentState = get();
        console.log('[Operation Store] 📊 Current state after setDriverInfo:', {
          driverId: currentState.driverId,
          driverName: currentState.driverName
        });
      },

      setInspectionCompleted: (recordId) => {
        console.log('[Operation Store] ✅ Inspection completed:', recordId);
        set({
          inspectionCompleted: true,
          inspectionRecordId: recordId,
          status: 'INSPECTING'
        });
        
        const currentState = get();
        console.log('[Operation Store] 📊 Current state after setInspectionCompleted:', {
          inspectionCompleted: currentState.inspectionCompleted,
          inspectionRecordId: currentState.inspectionRecordId,
          status: currentState.status
        });
      },

      startOperation: (operationId, startTime) => {
        console.log('[Operation Store] 🚀 START OPERATION CALLED:', operationId, 'startTime:', startTime);
        console.log('[Operation Store] 📋 Before update - current state:', get());
        
        // 🆕 運行開始時刻を保存（サーバーが返した実際の actualStartTime を優先。
        //    未指定の場合のみクライアント時刻でフォールバック）
        const resolvedStartTime = startTime || new Date().toISOString();
        
        set({
          operationId,
          operationStartTime: resolvedStartTime,  // 🆕
          status: 'IN_PROGRESS',
          phase: 'TO_LOADING' // 🔧 運行開始時は積込場所へ移動中から始まる
        });
        
        // デバッグ: 設定後の状態確認
        const currentState = get();
        console.log('[Operation Store] ✅ After update - operationId set to:', currentState.operationId);
        console.log('[Operation Store] ✅ After update - phase set to:', currentState.phase);
        console.log('[Operation Store] 📊 Full state after startOperation:', currentState);
        
        // localStorage確認
        setTimeout(() => {
          const stored = localStorage.getItem('operation-storage');
          console.log('[Operation Store] 💾 localStorage after startOperation:', stored);
        }, 100);
      },

      // 🆕 運行開始時刻の後付け設定（復元フォールバック用）
      //    operationStoreにoperationStartTimeが保存されていない古いセッションを
      //    バックエンドAPIから取得した実際の開始時刻で補完する場合に使用
      setOperationStartTime: (startTime) => {
        console.log('[Operation Store] 🕐 SET OPERATION START TIME (fallback復元):', startTime);
        set({ operationStartTime: startTime });
      },

      // 🔧 [経過時間再修正] 休憩開始時刻を永続化
      //    ブラウザを閉じても休憩中は経過時間が正しく休憩開始時点で停止したままになるよう、
      //    useRefではなくoperationStore（localStorage永続化）で管理する
      startBreakTimer: () => {
        const now = new Date().toISOString();
        console.log('[Operation Store] ☕ START BREAK TIMER:', now);
        set({ currentBreakStartTime: now });
      },

      // 🔧 [経過時間再修正] 休憩終了時、休憩秒数を合計へ加算しcurrentBreakStartTimeをクリア
      endBreakTimer: () => {
        const state = get();
        if (state.currentBreakStartTime) {
          const breakSec = Math.floor(
            (Date.now() - new Date(state.currentBreakStartTime).getTime()) / 1000
          );
          console.log('[Operation Store] ⏱️ END BREAK TIMER - 今回の休憩秒数:', breakSec);
          set({
            totalBreakSeconds: (state.totalBreakSeconds || 0) + Math.max(0, breakSec),
            currentBreakStartTime: null
          });
        } else {
          console.warn('[Operation Store] ⚠️ END BREAK TIMER: currentBreakStartTimeが未設定です');
        }
      },

      // フェーズ設定
      setPhase: (phase) => {
        console.log('[Operation Store] 🔄 SET PHASE CALLED:', phase);
        console.log('[Operation Store] 📋 Before update - current phase:', get().phase);
        
        set({ phase });
        
        const currentState = get();
        console.log('[Operation Store] ✅ After update - phase set to:', currentState.phase);
        console.log('[Operation Store] 📊 Full state after setPhase:', currentState);
        
        // localStorage確認
        setTimeout(() => {
          const stored = localStorage.getItem('operation-storage');
          console.log('[Operation Store] 💾 localStorage after setPhase:', stored);
        }, 100);
      },

      // 休憩前フェーズ保存 (2025-12-28)
      savePreviousPhase: (phase) => {
        console.log('[Operation Store] 💾 SAVE PREVIOUS PHASE CALLED:', phase);
        console.log('[Operation Store] 📋 Before save - current previousPhase:', get().previousPhase);
        
        set({ previousPhase: phase });
        
        const currentState = get();
        console.log('[Operation Store] ✅ After save - previousPhase set to:', currentState.previousPhase);
        console.log('[Operation Store] 📊 Full state after savePreviousPhase:', currentState);
        
        // localStorage確認
        setTimeout(() => {
          const stored = localStorage.getItem('operation-storage');
          console.log('[Operation Store] 💾 localStorage after savePreviousPhase:', stored);
        }, 100);
      },

      // 休憩回数インクリメント (2026-02-01)
      incrementBreakCount: () => {
        const newCount = get().breakCount + 1;
        console.log('[Operation Store] ☕ INCREMENT BREAK COUNT:', newCount);
        set({ breakCount: newCount });
      },

      // 積込場所設定（REQ-019: locationId も保存）
      setLoadingLocation: (location, locationId) => {
        console.log('[Operation Store] 📍 SET LOADING LOCATION:', location, locationId ?? '(id省略)');
        set({ loadingLocation: location, loadingLocationId: locationId ?? null });
        const currentState = get();
        console.log('[Operation Store] 📊 Full state after setLoadingLocation:', currentState);
      },

      // 🆕 積込場所設定（座標付き）
      setLoadingLocationWithCoords: (location, lat, lng) => {
        console.log('[Operation Store] 📍 SET LOADING LOCATION WITH COORDS:', location, lat, lng);
        set({ loadingLocation: location, loadingLocationLat: lat, loadingLocationLng: lng });
      },

      // 🆕 客先情報設定
      setCustomerInfo: (info) => {
        console.log('[Operation Store] 🏢 SET CUSTOMER INFO:', info);
        set({ customerId: info.customerId, customerName: info.customerName });
      },

      // ✅ Fix-S11-8: GPS累積走行距離セット
      setTotalDistanceKm: (km) => {
        console.log('[Operation Store] 🛣️ SET TOTAL DISTANCE KM:', km);
        set({ totalDistanceKm: km });
      },

      // 🆕 積降場所設定
      setUnloadingLocation: (location, locationId) => {
        console.log('[Operation Store] 📍 SET UNLOADING LOCATION:', location, locationId ?? '(id省略)');
        set({ 
          unloadingLocation: location,
          ...(locationId ? { unloadingLocationId: locationId } : {})
        });
        const currentState = get();
        console.log('[Operation Store] 📊 Full state after setUnloadingLocation:', currentState);
      },

      completeOperation: () => {
        console.log('[Operation Store] ✅ Complete operation');
        set({
          status: 'COMPLETED'
        });
      },

      resetOperation: () => {
        console.log('[Operation Store] 🔄 Reset operation');
        set({
          operationId: null,
          operationStartTime: null,  // 🆕
          currentBreakStartTime: null,  // 🔧 [経過時間再修正]
          totalBreakSeconds: 0,         // 🔧 [経過時間再修正]
          vehicleId: null,
          vehicleNumber: null,
          vehicleType: null,
          vehicleCapacity: null,  // REQ-004
          driverId: null,
          driverName: null,
          startMileage: null,
          status: 'IDLE',
          inspectionCompleted: false,
          inspectionRecordId: null,
          phase: 'TO_LOADING',
          breakCount: 0,
          previousPhase: null,
          loadingLocation: null,
          loadingLocationId: null,
          customerId: null,
          customerName: null,
          loadingLocationLat: null,   // 🆕
          loadingLocationLng: null,   // 🆕
          unloadingLocation: null,
          // ✅ FIX No.33: 前の運行の積込入力内容を引き継がない
          loadingHasCargoWork: undefined, loadingItemId: undefined, loadingCustomerId: undefined,
          loadingCustomItemName: undefined, loadingSelectedItemIds: undefined,
          loadingQuantity: undefined, loadingNotes: undefined,
        } as any);
      }
    }),
    {
      name: 'operation-storage',
      partialize: (state) => {
        console.log('[Operation Store] 💾 Partialize called - saving state:', {
          operationId: state.operationId,
          vehicleId: state.vehicleId,
          status: state.status,
          phase: state.phase, // 🔧 フェーズも保存
          breakCount: state.breakCount,       // 🔧 休憩回数も永続化 (2026-02-01)
          previousPhase: state.previousPhase // 🆕 休憩前フェーズも保存 (2025-12-28)
        });
        
        return {
          operationId: state.operationId,
          operationStartTime: state.operationStartTime,  // 🆕 経過時間の正確な復元に必須
          currentBreakStartTime: state.currentBreakStartTime,  // 🔧 [経過時間再修正] 休憩中の停止復元に必須
          totalBreakSeconds: state.totalBreakSeconds,          // 🔧 [経過時間再修正] 休憩除外時間の復元に必須
          vehicleId: state.vehicleId,
          vehicleNumber: state.vehicleNumber,
          vehicleType: state.vehicleType,
          vehicleCapacity: state.vehicleCapacity,  // REQ-004
          driverId: state.driverId,
          driverName: state.driverName,
          startMileage: state.startMileage,
          status: state.status,
          inspectionCompleted: state.inspectionCompleted,
          inspectionRecordId: state.inspectionRecordId,
          // 🆕 フェーズ管理フィールドも永続化
          phase: state.phase,
          breakCount: state.breakCount,            // 🔧 休憩回数も永続化 (2026-02-01)
          previousPhase: state.previousPhase, // 🆕 休憩前フェーズも永続化 (2025-12-28)
          loadingLocation: state.loadingLocation,
          loadingLocationId: state.loadingLocationId,  // REQ-019
          customerId: state.customerId,
          customerName: state.customerName,
          totalDistanceKm: state.totalDistanceKm, // ✅ Fix-S11-8
          loadingLocationLat: state.loadingLocationLat,   // 🆕
          loadingLocationLng: state.loadingLocationLng,   // 🆕
          unloadingLocation: state.unloadingLocation,
          // ✅ FIX No.19: 積込/荷降パターンを永続化（従来は再読込で既定値P2/U2に戻り、
          //   P1/P3・U1/U3の車両がP2/U2の動作になっていた）
          loadingPattern: state.loadingPattern,
          unloadingPattern: state.unloadingPattern,
          // ✅ FIX No.33: 積込入力で選んだ内容（P1の荷役フラグ等）も再読込で消えないよう永続化
          loadingHasCargoWork: (state as any).loadingHasCargoWork,
          loadingItemId: (state as any).loadingItemId,
          loadingCustomerId: (state as any).loadingCustomerId,
          loadingCustomItemName: (state as any).loadingCustomItemName,
          loadingSelectedItemIds: (state as any).loadingSelectedItemIds,
          loadingQuantity: (state as any).loadingQuantity,
          loadingNotes: (state as any).loadingNotes,
        } as any;
      },
      // デバッグ: 復元時のログ
      onRehydrateStorage: () => {
        console.log('[Operation Store] 🔄 Rehydration started');
        return (state, error) => {
          if (error) {
            console.error('[Operation Store] ❌ Rehydration error:', error);
          } else {
            console.log('[Operation Store] ✅ Rehydration complete:', state);
          }
        };
      }
    }
  )
);

// デバッグ用: グローバルアクセス
if (typeof window !== 'undefined') {
  (window as any).operationStore = useOperationStore;
  console.log('[Operation Store] 🔍 Debug: window.operationStore available');
  console.log('[Operation Store] 🔍 Usage: window.operationStore.getState()');
}

/**
 * 🔧 修正内容 (2025-12-12)
 * 
 * 1. OperationPhase 型定義を追加:
 *    - 'TO_LOADING' | 'AT_LOADING' | 'TO_UNLOADING' | 'AT_UNLOADING' | 'BREAK' | 'REFUEL'
 * 
 * 2. OperationState インターフェースに追加:
 *    - phase: OperationPhase - 現在の運行フェーズ
 *    - loadingLocation: string | null - 積込場所名
 *    - unloadingLocation: string | null - 積降場所名
 * 
 * 3. アクション追加:
 *    - setPhase(phase: OperationPhase) - フェーズを更新
 *    - setLoadingLocation(location: string) - 積込場所名を設定
 *    - setUnloadingLocation(location: string) - 積降場所名を設定
 * 
 * 4. startOperation アクションを修正:
 *    - phase: 'TO_LOADING' を初期値として設定
 * 
 * 5. resetOperation アクションを修正:
 *    - phase, loadingLocation, unloadingLocation もリセット
 * 
 * 6. partialize 設定を修正:
 *    - phase, loadingLocation, unloadingLocation も永続化対象に追加
 * 
 * これにより、フェーズ状態がブラウザ再読み込み後も保持されます。
 * 
 * 🔧 修正内容 (2025-12-28) - 休憩終了時のフェーズ復元対応
 * 
 * 1. OperationState インターフェースに追加:
 *    - previousPhase: OperationPhase | null - 休憩前のフェーズを記憶
 * 
 * 2. アクション追加:
 *    - savePreviousPhase(phase: OperationPhase) - 休憩前のフェーズを保存
 * 
 * 3. 初期状態に追加:
 *    - previousPhase: null
 * 
 * 4. resetOperation アクションを修正:
 *    - previousPhase もリセット
 * 
 * 5. partialize 設定を修正:
 *    - previousPhase も永続化対象に追加
 * 
 * これにより、休憩開始時に現在のフェーズを保存し、休憩終了時に元のフェーズに戻せるようになります。
 * 例: TO_LOADING → 休憩開始(previousPhase=TO_LOADING保存) → BREAK → 休憩終了 → TO_LOADING復元
 */